import { useState } from 'react';
import {
  Card, BlockStack, InlineStack, Text, Badge, Banner, Button, TextField, Box,
  Thumbnail, Collapsible, Divider,
} from '@shopify/polaris';
import type { Wheel, WheelPayload } from '../api/wheels';
import { upsizeWpImage } from '../lib/wpImageUpsize';

type FormShape = Partial<Wheel> & { bolt_pattern_raw?: string };

interface Props {
  wheel: Wheel | undefined;
  form: FormShape;
  onChangeField: (key: keyof WheelPayload, value: string | null) => void;
  onVerify: (verified: boolean) => void;
  verifyLoading?: boolean;
}

/**
 * Shopify push-array order (matches backend buildShopifyPushImages):
 *   1. image_large    (primary; Wheel Pros hero / ATD large)
 *   2. image_small    (ATD small / WP secondary)
 *   3. image_thumbnail (ATD thumbnail)
 *   4. image_url_1    (WP angle A1)
 *   5. image_url_2    (WP angle A2)
 *   6. image_url_3    (WP angle A3)
 *   7. image_url_4    (WP FACE view)
 * De-duplicated on exact URL match, empties skipped.
 */
const SLOT_ORDER: Array<{
  key: keyof WheelPayload;
  label: string;
  note: string;
}> = [
  { key: 'image_large',     label: 'image_large',     note: 'Primary (Shopify card). WP hero on media.wheelpros.com / ATD large.' },
  { key: 'image_small',     label: 'image_small',     note: 'ATD small / WP secondary angle.' },
  { key: 'image_thumbnail', label: 'image_thumbnail', note: 'ATD thumbnail.' },
  { key: 'image_url_1',     label: 'image_url_1',     note: 'Wheel Pros gallery angle A1.' },
  { key: 'image_url_2',     label: 'image_url_2',     note: 'Wheel Pros gallery angle A2.' },
  { key: 'image_url_3',     label: 'image_url_3',     note: 'Wheel Pros gallery angle A3.' },
  { key: 'image_url_4',     label: 'image_url_4',     note: 'Wheel Pros FACE view.' },
];

type QualityStatus = 'CRITICAL' | 'WARNING' | 'OK' | 'GOOD';

/**
 * Image-set quality classification used both in the status badge and the
 * push-blocking guard. Mirrors a backend helper of the same name so admin
 * + push pipeline stay in sync.
 */
export function detectImageQuality(images: string[]): { count: number; status: QualityStatus } {
  const count = images.length;
  let status: QualityStatus;
  if (count === 0) status = 'CRITICAL';
  else if (count === 1) status = 'WARNING';
  else if (count <= 3) status = 'OK';
  else status = 'GOOD';
  return { count, status };
}

/** Polaris tone for the quality badge. */
function qualityTone(s: QualityStatus): 'critical' | 'warning' | 'success' {
  return s === 'CRITICAL' ? 'critical' : s === 'WARNING' ? 'warning' : 'success';
}

/**
 * Larger primary-image preview. Uses a plain <img> at 320×320 so the
 * upsized assets.wheelpros.com payload (?size=1500) has somewhere to
 * land — Polaris Thumbnail's max size is 80px, which would waste the
 * extra resolution. onError swaps in a Polaris Thumbnail of the same
 * URL so we keep Polaris's broken-image placeholder behavior.
 */
function PrimaryWheelImage({ src, alt }: { src: string; alt: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) return <Thumbnail source={src} alt={alt} size="large" />;
  return (
    <div
      style={{
        width: 320, height: 320,
        background: 'var(--p-color-bg-surface-secondary)',
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setErrored(true)}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </div>
  );
}

/**
 * Per-wheel image gallery on the admin Wheel Detail page.
 *
 * Each vendor image slot is edited individually (they're distinct DB columns,
 * not a JSONB array). The "ordered push array" preview shows exactly what
 * Shopify will receive — computed with the same rule as the backend helper
 * `buildShopifyPushImages`.
 *
 * Admins must click "Mark images verified" before any Shopify push. Any edit
 * to an image field resets verification on the server side (see PATCH route).
 */
export function WheelImageGallery({ wheel, form, onChangeField, onVerify, verifyLoading }: Props) {
  const [showRaw, setShowRaw] = useState(false);

  const val = (k: keyof WheelPayload) =>
    (form[k] as string | null | undefined) ?? (wheel ? (wheel[k] as any) : null) ?? '';
  const display = (k: keyof WheelPayload): string | null => {
    const v = val(k);
    return typeof v === 'string' && v.length > 0 ? v : null;
  };

  // Push-order preview (matches backend buildShopifyPushImages)
  const pushOrder = (() => {
    const seen = new Set<string>();
    const out: Array<{ key: keyof WheelPayload; url: string }> = [];
    for (const slot of SLOT_ORDER) {
      const url = display(slot.key);
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push({ key: slot.key, url });
    }
    return out;
  })();

  // Push-ready array of {src, position} objects — what Shopify would receive
  const pushArray = pushOrder.map((p, i) => ({ src: p.url, position: i + 1 }));
  const quality = detectImageQuality(pushOrder.map((p) => p.url));

  // Count how many of the 7 vendor slots had a value vs were empty
  const slotsFilled = SLOT_ORDER.filter((s) => display(s.key)).length;
  const slotsTotal = SLOT_ORDER.length;

  const brandLogo = display('brand_logo_url');
  const verified = !!wheel?.images_verified;
  const verifiedAt = wheel?.images_verified_at;
  const verifiedBy = wheel?.images_verified_by;
  const source = wheel?.source_guide ?? null;

  // Migration 015 hasn't been applied yet if the images_verified column is
  // absent from the GET response (SELECT * returns only columns that exist).
  // Render a read-only placeholder rather than letting the user hit endpoints
  // that will fail with "column does not exist" at the DB layer.
  const migrationApplied = wheel !== undefined && 'images_verified' in (wheel as object);

  // Dirty = any image field differs between form and server row
  const editedKeys = SLOT_ORDER.map((s) => s.key).concat(['brand_logo_url']);
  const editsDirty = wheel
    ? editedKeys.some((k) => {
        const a = (wheel[k] as any) ?? null;
        const b = form[k] === undefined ? a : (form[k] ?? null);
        return (a || '') !== (b || '');
      })
    : false;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">Images</Text>
          <InlineStack gap="200" blockAlign="center">
            {source && <Badge tone="info">{`Source: ${source}`}</Badge>}
            <Badge tone={qualityTone(quality.status)}>
              {`${quality.count} ${quality.count === 1 ? 'image' : 'images'} · ${quality.status}`}
            </Badge>
            {!migrationApplied
              ? <Badge tone="warning">Schema pending</Badge>
              : verified
                ? <Badge tone="success">Verified for Shopify push</Badge>
                : <Badge tone="attention">Not verified</Badge>}
          </InlineStack>
        </InlineStack>

        {!migrationApplied && (
          <Banner tone="warning" title="Image verification schema not yet deployed">
            Migration 015_wheel_images.sql has not been applied to production
            yet. The gallery below is read-only until the DB schema catches up.
            Existing image_large / image_small / image_thumbnail values still
            display; numbered gallery slots, brand logo, and the Mark-verified
            button are disabled.
          </Banner>
        )}

        {pushOrder.length === 0 && (
          <Banner tone="critical" title="No images">
            This wheel has no images in any slot. Shopify push is blocked until at
            least one image URL is filled in below.
          </Banner>
        )}
        {pushOrder.length === 1 && (
          <Banner tone="warning" title="Only one image">
            Only one image is set. Confirm this is the full vendor set before
            verifying — most wheels ship with 2–5 angles.
          </Banner>
        )}
        {migrationApplied && quality.status !== 'CRITICAL' && slotsFilled < slotsTotal && (
          <Banner tone="info" title={`Vendor feed only provided ${slotsFilled} of ${slotsTotal} possible slots`}>
            <Text as="p" variant="bodySm">
              The Wheel Pros CSV in S3 only exposes <code>image_large</code>, <code>image_small</code>,
              <code> image_thumbnail</code> — the <code>image_url_1..4</code> numbered angles are
              dropped by the upstream pre-processor. Dealerline.wheelpros.com shows more images
              than the feed because it queries Wheel Pros' internal media database directly.
            </Text>
          </Banner>
        )}
        {editsDirty && verified && (
          <Banner tone="warning">
            Unsaved image edits. Save first; verification will reset and must be
            re-confirmed.
          </Banner>
        )}

        {pushOrder.length > 0 && (
          <Box>
            <BlockStack gap="300">
              <InlineStack gap="200" align="space-between" blockAlign="center">
                <Text as="p" variant="headingSm">
                  {`Gallery · ${pushOrder.length} ${pushOrder.length === 1 ? 'angle' : 'angles'} available`}
                </Text>
                <Badge tone={qualityTone(quality.status)}>
                  {`${pushOrder.length} of ${SLOT_ORDER.length} slots filled`}
                </Badge>
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Wheel Pros gallery URLs are upsized at render time
                (?size=500 → ?size=1500) — DB rows untouched. Position 1 =
                primary product card image; remaining angles stack as
                thumbnails. De-duplicated across slots.
              </Text>

              {/* Primary preview — bigger frame than the original
                  Thumbnail so the upsized image has somewhere to land.
                  Uses a plain <img> with onError fallback so a broken
                  URL renders the existing Polaris Thumbnail (Polaris
                  supplies its own image-error placeholder). */}
              <Box>
                <BlockStack gap="100">
                  <PrimaryWheelImage
                    src={upsizeWpImage(pushOrder[0].url) ?? pushOrder[0].url}
                    alt={String(pushOrder[0].key)}
                  />
                  <Text as="span" variant="bodySm" tone="subdued">
                    {`Position 1 (primary) · ${String(pushOrder[0].key)}`}
                  </Text>
                </BlockStack>
              </Box>

              {pushOrder.length > 1 && (
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {`Additional angles (${pushOrder.length - 1})`}
                  </Text>
                  <InlineStack gap="200" wrap>
                    {pushOrder.slice(1).map((p, idx) => {
                      const upsized = upsizeWpImage(p.url) ?? p.url;
                      return (
                        <Box key={`${p.key}-${idx}`}>
                          <BlockStack gap="050">
                            <Thumbnail source={upsized} alt={String(p.key)} size="large" />
                            <Text as="span" variant="bodySm" tone="subdued">
                              {`Position ${idx + 2}`}
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                              {String(p.key)}
                            </Text>
                          </BlockStack>
                        </Box>
                      );
                    })}
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Box>
        )}

        <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="300">
          <Button
            variant="tertiary"
            onClick={() => setShowRaw((v) => !v)}
            disclosure={showRaw ? 'up' : 'down'}
          >
            {`Raw DB image fields (${slotsFilled}/${slotsTotal} populated)`}
          </Button>
          <Collapsible id="raw-image-fields" open={showRaw}>
            <Box paddingBlockStart="300">
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  Direct dump of every image column on this wheel_products row. Helps
                  debug feed-vs-UI discrepancies (e.g. why Dealerline shows more
                  images than what the CSV ingests).
                </Text>
                {SLOT_ORDER.map((slot) => {
                  const v = display(slot.key);
                  return (
                    <InlineStack key={`raw-${slot.key}`} gap="200" blockAlign="start" wrap={false}>
                      <Box minWidth="160px">
                        <Text as="span" variant="bodySm" fontWeight="semibold">{slot.label}</Text>
                      </Box>
                      <Box>
                        {v
                          ? <Text as="span" variant="bodySm" breakWord>{v}</Text>
                          : <Text as="span" variant="bodySm" tone="subdued">(null)</Text>}
                      </Box>
                    </InlineStack>
                  );
                })}
                <InlineStack gap="200" blockAlign="start" wrap={false}>
                  <Box minWidth="160px">
                    <Text as="span" variant="bodySm" fontWeight="semibold">brand_logo_url</Text>
                  </Box>
                  <Box>
                    {brandLogo
                      ? <Text as="span" variant="bodySm" breakWord>{brandLogo}</Text>
                      : <Text as="span" variant="bodySm" tone="subdued">(null)</Text>}
                  </Box>
                </InlineStack>
                <Divider />
                <Text as="p" variant="bodySm" fontWeight="semibold">buildShopifyPushImages() output</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Exact array shape that will be sent to Shopify productCreateMedia.
                </Text>
                <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                  <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{JSON.stringify(pushArray, null, 2)}
                  </pre>
                </Box>
              </BlockStack>
            </Box>
          </Collapsible>
        </Box>

        <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="300">
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Per-slot URLs</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Each vendor field maps to its own column. Leave empty if the vendor
              feed didn't provide a value — do not paste placeholder or fabricated
              URLs.
            </Text>
            {SLOT_ORDER.map((slot) => {
              // The numbered angle slots (image_url_1..4) require migration 015.
              // image_large/image_small/image_thumbnail existed before 015, so
              // they remain editable even in schema-pending mode.
              const slotNeedsMigration = slot.key.toString().startsWith('image_url_');
              const disabled = !migrationApplied && slotNeedsMigration;
              return (
                <BlockStack key={slot.key} gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    {display(slot.key) && (
                      <Thumbnail
                        source={upsizeWpImage(display(slot.key)) ?? display(slot.key)!}
                        alt={slot.label}
                        size="small"
                      />
                    )}
                    <Box minWidth="0">
                      <div style={{ width: 500, maxWidth: '100%' }}>
                        <TextField
                          label={slot.label}
                          helpText={disabled ? 'Disabled — needs migration 015' : slot.note}
                          value={val(slot.key) as string}
                          onChange={(v) => onChangeField(slot.key, v.trim() ? v.trim() : null)}
                          autoComplete="off"
                          disabled={disabled}
                        />
                      </div>
                    </Box>
                  </InlineStack>
                </BlockStack>
              );
            })}
          </BlockStack>
        </Box>

        <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="300">
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Brand / logo image</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Distinct from product media. Not pushed as Shopify product image —
              used only by the storefront wheel-detail page next to the style name.
            </Text>
            <InlineStack gap="200" blockAlign="center">
              {brandLogo && <Thumbnail source={brandLogo} alt="Brand logo" size="small" />}
              <Box minWidth="0">
                <div style={{ width: 500, maxWidth: '100%' }}>
                  <TextField
                    label="Brand logo URL"
                    labelHidden
                    value={(val('brand_logo_url') as string) ?? ''}
                    onChange={(v) => onChangeField('brand_logo_url', v.trim() ? v.trim() : null)}
                    autoComplete="off"
                    placeholder="https://…/brand-logo.png"
                    disabled={!migrationApplied}
                    helpText={!migrationApplied ? 'Disabled — needs migration 015' : undefined}
                  />
                </div>
              </Box>
            </InlineStack>
          </BlockStack>
        </Box>

        <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="300">
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Verification</Text>
            {!migrationApplied ? (
              <Text as="p" variant="bodySm" tone="subdued">
                Verification unavailable until migration 015 is applied to the
                database. The Shopify push pipeline will block any wheel whose
                images aren't verified once the schema is live.
              </Text>
            ) : verified ? (
              <BlockStack gap="100">
                <Text as="p" variant="bodySm">
                  Verified {verifiedAt ? new Date(verifiedAt).toLocaleString() : ''}
                  {verifiedBy ? ` by ${verifiedBy}` : ''}.
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Shopify push will use the {pushOrder.length}-image array above in
                  that exact order.
                </Text>
                <InlineStack>
                  <Button loading={verifyLoading} onClick={() => onVerify(false)}>
                    Un-verify
                  </Button>
                </InlineStack>
              </BlockStack>
            ) : (
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  Confirm the image set is correct, ordered, and represents the
                  vendor's full output. The Shopify push pipeline will block any
                  wheel without verification.
                </Text>
                <InlineStack>
                  <Button
                    variant="primary"
                    loading={verifyLoading}
                    disabled={pushOrder.length === 0 || editsDirty}
                    onClick={() => onVerify(true)}
                  >
                    Mark images verified
                  </Button>
                </InlineStack>
                {editsDirty && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    Save pending image changes before verifying.
                  </Text>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Box>
      </BlockStack>
    </Card>
  );
}
